import { useRef, useState } from 'react'
import { Alert, Box, Button, Link, Menu, MenuItem, TextField, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useNavigate } from '../../lib/router'
import type {
  CheckDescriptor, Country, IngestDetail, Preset, ReferenceLayer, RunConfig,
} from '../../api/types'
import {
  useCreateRegion, useCreateRun, usePresets, useRegions, useRemoveIngest, useSavePreset,
  useSession, useUpdateFieldMapping, useUpdateIngestRefs, useUpdateRegion, useUploadToRegion,
} from '../../api/hooks'
import { Eyebrow } from '../../components/primitives'
import { useUnsavedWork } from '../../components/NavigationGuard'
import { useGoHome } from '../../lib/home'
import { BUILT_IN_PRESET, useRunConfig } from '../../lib/runConfig'
import { ReferenceFields, useMissingReferences } from '../../components/ReferenceFields'
import { formatDateTime, listNames, num } from '../../lib/format'
import { ApiError } from '../../api/client'
import { CountryPicker } from '../../components/CountryPicker'
import { tokens } from '../../theme'
import { ChecksPanel } from './ChecksPanel'
import { UploadCard, type UploadItem } from './UploadCard'
import { FieldMapping } from './FieldMapping'
import { estimateRun } from './checkMeta'

const STEPS = ['Region', 'Fields', 'Checks', 'Results'] as const

function Stepper({ step }: { step: number }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, font: `500 12px ${tokens.font.sans}` }}>
      {STEPS.map((label, i) => {
        const done = i < step
        const current = i === step
        return (
          <Box
            key={label}
            sx={{
              px: 2.5, py: 1.25, borderRadius: tokens.radius.pill, whiteSpace: 'nowrap',
              backgroundColor: done ? tokens.color.brandDeep : 'transparent',
              color: done ? tokens.color.ink0 : current ? tokens.color.brandDeep : 'text.secondary',
              border: done
                ? 'none'
                : `1px solid ${current ? tokens.color.brandDeep : tokens.color.ink300}`,
            }}
          >
            {i + 1} · {label}
          </Box>
        )
      })}
    </Box>
  )
}

interface Props {
  checks: CheckDescriptor[]
  layers: ReferenceLayer[]
  countries: Country[]
  defaultConfig: RunConfig
  switcher: React.ReactNode
}

/**
 * Upload a file into a region, map its columns, choose checks, run.
 *
 * The region comes first because a plot has to live somewhere — there is no
 * ingest to park it in while you decide. The upload is held in state
 * rather than fetched back by id: an ingest is provenance, not a place, so
 * there is no route that returns one.
 */
export function UploadRun({
  checks, layers, countries, defaultConfig, switcher,
}: Props) {
  const navigate = useNavigate()
  const { data: regions = [] } = useRegions()
  const createRegion = useCreateRegion()
  // Arriving from a region preselects it; otherwise nothing is chosen and
  // the dropzone stays shut until something is.
  const [params] = useSearchParams()
  const [regionId, setRegionId] = useState<string>(params.get('region') ?? '')
  const [newRegion, setNewRegion] = useState('')
  const [newCountry, setNewCountry] = useState('')
  const updateRegion = useUpdateRegion()
  const region = regions.find((r) => r.id === regionId)
  // The border every plot in the region is judged against, so the plots'
  // country. A region without one has to be given one before a file lands.
  const regionCountry = region?.country_code ?? null
  const upload = useUploadToRegion()
  const updateIngestRefs = useUpdateIngestRefs()
  /**
   * The host's own references for these files (a contract, say), given to
   * every plot in them. Sent with each file, and to the files already read
   * when one changes.
   */
  const [refs, setRefs] = useState<Record<string, string>>({})
  const refsRef = useRef(refs)
  refsRef.current = refs
  const missingRefs = useMissingReferences(refs)
  const synced = useSession().data?.directory === 'synced'
  const updateMapping = useUpdateFieldMapping()
  const removeIngest = useRemoveIngest()
  const createRun = useCreateRun()
  const { data: presets = [] } = usePresets()
  const savePreset = useSavePreset()

  const { config, preset: activePreset, save: saveConfig } = useRunConfig(defaultConfig)
  // Changing a check after loading a preset is no longer that preset as saved.
  const setConfig = (next: RunConfig) =>
    saveConfig(next, activePreset.endsWith(' (edited)') ? activePreset : `${activePreset} (edited)`)
  const [presetAnchor, setPresetAnchor] = useState<HTMLElement | null>(null)
  const [savingPreset, setSavingPreset] = useState(false)
  const [presetName, setPresetName] = useState('')

  /**
   * The files going into the region. Each is its own upload with its own
   * column mapping; `draft` is the local echo of a mapping change, so the
   * selects respond before the server answers.
   */
  const [items, setItems] = useState<Item[]>([])
  const itemsRef = useRef(items)
  itemsRef.current = items
  const [selected, setSelected] = useState<string | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const patchItem = (key: string, change: Partial<Item>) =>
    setItems((current) => current.map((i) => (i.key === key ? { ...i, ...change } : i)))

  const read = items.filter((i) => i.state === 'read')
  const reading = items.some((i) => i.state === 'reading')
  const current = read.find((i) => i.key === selected) ?? read[0]
  const matches = current ? matchesOf(current) : []
  // CO, SU and Certificate may stay unmapped: the API says which (optional_fields).
  const optionalFields = new Set(current?.detail?.optional_fields ?? [])
  const waiting = read.filter((i) => unmappedOf(i).length > 0)
  const features = read.reduce((n, i) => n + (i.detail?.ingest.feature_count ?? 0), 0)

  // The region's country too: within-country judges against it, and without
  // one the run used to start anyway and skip that check (AG-76).
  const unknownSuppliers = synced
    ? [...new Set(read.flatMap((i) => i.detail?.derived?.unknown_suppliers ?? []))]
    : []

  const ready =
    read.length > 0 && !reading && waiting.length === 0 && Boolean(regionCountry) && missingRefs.length === 0 &&
    read.every((i) => Boolean(i.detail?.ingest.country_code))
  const enabledKeys = checks.filter((c) => config.checks[c.key]?.enabled !== false).map((c) => c.key)

  const step = !read.length ? 0 : waiting.length ? 1 : 2

  // Files have been read but no run started yet; leaving now throws that away.
  useUnsavedWork(
    read.length > 0 && !createRun.isSuccess,
    `${read.length === 1 ? read[0]!.name : `${read.length} files`} imported but not validated yet.`,
  )

  const commitMapping = (item: Item, next: Record<string, string | null>) => {
    patchItem(item.key, { draft: next })
    updateMapping.mutate(
      { ingestId: item.detail!.ingest.id, mapping: next },
      { onSuccess: (detail) => patchItem(item.key, { detail, draft: undefined }) },
    )
  }

  /**
   * Read one at a time, in the order given: each is a request of its own
   * (and a Lambda of its own, deployed), and the list fills in as they land.
   * A file whose columns are exactly those of one already mapped is given
   * that mapping, so ten files from one supplier are mapped once.
   */
  /**
   * A file already in the region (the same bytes, its plots still there),
   * waiting for the user to say what to do: uploading it again put every
   * plot in the region twice, and the checks then counted them all (AG-90).
   */
  const [duplicates, setDuplicates] = useState<{ key: string; file: File; of: DuplicateOf }[]>([])
  const [resolving, setResolving] = useState(false)

  const uploadOne = async (file: File, key: string, allowDuplicate = false) => {
      patchItem(key, { state: 'reading', error: undefined })
      const form = new FormData()
      const given = Object.fromEntries(Object.entries(refsRef.current).filter(([, v]) => v))
      if (Object.keys(given).length) form.append('refs', JSON.stringify(given))
      if (allowDuplicate) form.append('allow_duplicate', 'true')
      form.append('file', file)
      try {
        let detail = await upload.mutateAsync({ regionId, form })
        const twin = itemsRef.current.find(
          (i) => i.state === 'read' && i.detail && sameColumns(i.detail, detail),
        )
        if (twin) {
          const mapping = mappingOf(twin)
          const differs = detail.field_matches.some((m) => (mapping[m.field] ?? null) !== m.column)
          if (differs) detail = await updateMapping.mutateAsync({ ingestId: detail.ingest.id, mapping })
        }
        patchItem(key, { state: 'read', detail })
        setSelected((s) => s ?? key)
      } catch (err) {
        const of = err instanceof ApiError && err.status === 409
          ? (err.body as { duplicate_of?: DuplicateOf } | undefined)?.duplicate_of
          : undefined
        patchItem(key, { state: 'failed', error: (err as Error).message })
        if (of) setDuplicates((d) => [...d, { key, file, of }])
      }
  }

  const addFiles = async (files: File[]) => {
    const added: Item[] = files.map((f) => ({
      key: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name, size: f.size, state: 'reading',
    }))
    setItems((current) => [...current, ...added])
    for (const [n, file] of files.entries()) await uploadOne(file, added[n]!.key)
  }

  const duplicate = duplicates[0]
  const resolveDuplicate = async (choice: 'replace' | 'again' | 'skip') => {
    if (!duplicate) return
    setResolving(true)
    try {
      if (choice === 'skip') {
        setItems((current) => current.filter((i) => i.key !== duplicate.key))
      } else {
        if (choice === 'replace') {
          await removeIngest.mutateAsync(duplicate.of.id)
          // The earlier upload may be one read on this screen.
          setItems((current) => current.filter((i) => i.detail?.ingest.id !== duplicate.of.id))
        }
        await uploadOne(duplicate.file, duplicate.key, choice === 'again')
      }
      setDuplicates((d) => d.slice(1))
    } finally {
      setResolving(false)
    }
  }

  const removeItem = (item: Item) => {
    const drop = () => {
      setItems((current) => current.filter((i) => i.key !== item.key))
      setSelected((s) => (s === item.key ? null : s))
    }
    if (item.state !== 'read') return drop()
    setRemoving(item.key)
    removeIngest.mutate(item.detail!.ingest.id, { onSuccess: drop, onSettled: () => setRemoving(null) })
  }

  /**
   * The way out (AG-79). Files already read have been loaded into the
   * region, so leaving asks whether to take them out again or keep them.
   */
  const goHome = useGoHome()
  const [cancelling, setCancelling] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const cancel = () => (read.length ? setCancelling(true) : goHome(true))
  const discardAndLeave = async () => {
    setDiscarding(true)
    try {
      for (const item of read) await removeIngest.mutateAsync(item.detail!.ingest.id)
      goHome(true)
    } finally {
      setDiscarding(false)
    }
  }

  const applyPreset = (preset: Preset) => {
    // Keep the layers already chosen on this screen unless the preset names one.
    saveConfig({
      checks: preset.config.checks,
      layers: { ...config.layers, ...Object.fromEntries(Object.entries(preset.config.layers ?? {}).filter(([, v]) => v)) },
    }, preset.name)
    setPresetAnchor(null)
  }

  return (
    <>
      <Box sx={{ flex: 1, overflow: 'auto', px: 8, pt: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 6 }}>
          <Box>
            <Eyebrow sx={{ mb: 2 }}>New validation run</Eyebrow>
            <Typography variant="h1">Check plot polygons before submission</Typography>
          </Box>
          <Stepper step={step} />
        </Box>

        {switcher}

        {removeIngest.isError && <Alert severity="error">{(removeIngest.error as Error).message}</Alert>}
        {createRun.isError && <Alert severity="error">{(createRun.error as Error).message}</Alert>}
        {/* Suppliers come only from the organisation's own list: a name the
            file gives that is not on it is left unlinked, to be put right. */}
        {unknownSuppliers.length > 0 && (
          <Alert severity="warning">
            Not one of your suppliers: {listNames(unknownSuppliers)}. Those plots keep the name from the file but
            belong to no supplier until it is added to your list, or they are given one on the plot screen.
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,440px) minmax(0,1fr)', gap: 5, alignItems: 'start', pb: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {/*
              A plot has to live somewhere, so the region is chosen before a
              file is read rather than after it — there is no dataset to hold
              it in the meantime.
            */}
            <RegionPicker
              regions={regions}
              countries={countries}
              value={regionId}
              onChange={setRegionId}
              newName={newRegion}
              onNewName={setNewRegion}
              newCountry={newCountry}
              onNewCountry={setNewCountry}
              creating={createRegion.isPending}
              disabled={items.length > 0}
              onCreate={() =>
                createRegion.mutate(
                  { name: newRegion.trim(), country_code: newCountry },
                  { onSuccess: (r) => { setRegionId(r.id); setNewRegion(''); setNewCountry('') } },
                )
              }
              missingCountry={Boolean(region) && !regionCountry}
              settingCountry={updateRegion.isPending}
              onSetCountry={(code) => region && updateRegion.mutate({ id: region.id, country_code: code })}
            />
            <ReferenceFields
              values={refs}
              onChange={(key, value) => {
                setRefs((r) => ({ ...r, [key]: value ?? '' }))
                for (const item of itemsRef.current) {
                  if (item.state === 'read' && item.detail) {
                    updateIngestRefs.mutate({ ingestId: item.detail.ingest.id, refs: { [key]: value } })
                  }
                }
              }}
            />
            {updateIngestRefs.isError && <Alert severity="error">{(updateIngestRefs.error as Error).message}</Alert>}
            <UploadCard
              disabled={!regionId || !regionCountry}
              disabledReason={regionId ? 'Choose the region\u2019s country first' : undefined}
              regionCountry={regionCountry}
              uploads={items.map((i) => ({ ...i, unmapped: unmappedOf(i) }))}
              selected={current?.key ?? null}
              onSelect={setSelected}
              onRemove={(key) => { const item = items.find((i) => i.key === key); if (item) removeItem(item) }}
              removing={removing}
              countries={countries}
              onFiles={addFiles}
            />
            {current?.detail && (
              <FieldMapping
                matches={matches}
                fileName={read.length > 1 ? current.name : undefined}
                optional={{
                  ...(optionalFields.has('CO') ? { CO: 'From the region' } : {}),
                  ...(optionalFields.has('SU') ? { SU: 'Not in this file' } : {}),
                  ...(optionalFields.has('Certificate') ? { Certificate: 'Not in this file' } : {}),
                }}
                columns={current.detail.ingest.source_columns}
                disabled={updateMapping.isPending}
                onChange={(field, column) => commitMapping(current, { ...mappingOf(current), [field]: column })}
              />
            )}
          </Box>

          <ChecksPanel
            checks={checks}
            config={config}
            layers={layers}
            onChange={setConfig}
            header={
              <Link onClick={(e) => setPresetAnchor(e.currentTarget)} sx={{ font: `500 13px ${tokens.font.sans}` }}>
                Load preset · {activePreset}
              </Link>
            }
          />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 'none', height: 64, borderTop: `1px solid ${tokens.color.ink200}`,
          backgroundColor: 'background.paper', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', px: 8,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {!read.length
            ? (reading ? 'Reading the file… ' : 'Upload files to begin.')
            : reading
              ? 'Reading files… '
              : waiting.length
                ? read.length === 1
                  ? `Map the ${unmappedOf(waiting[0]!).join(', ')} field${unmappedOf(waiting[0]!).length > 1 ? 's' : ''} to continue. `
                  : `Map the remaining fields in ${listNames(waiting.map((i) => i.name))} to continue. `
                : !regionCountry
                  ? 'Give the region a country to continue. '
                  : missingRefs.length
                    ? `Add the ${listNames(missingRefs)} to continue. `
                    : ''}
          {read.length > 0 &&
            `${enabledKeys.length} checks · est. ${estimateRun(enabledKeys, features)} for ${num(features)} polygons` +
            `${read.length > 1 ? ` in ${read.length} files` : ''}.`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="text" onClick={cancel} disabled={createRun.isPending}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={() => setSavingPreset(true)}>
            Save as preset
          </Button>
          <Button
            disabled={!ready || createRun.isPending}
            data-testid="run-validation"
            onClick={() =>
              regionId &&
              createRun.mutate(
                { region_id: regionId, config },
                { onSuccess: () => navigate(`/regions/${regionId}`) },
              )
            }
          >
            {createRun.isPending ? 'Starting…' : 'Run validation'}
          </Button>
        </Box>
      </Box>

      <Menu anchorEl={presetAnchor} open={Boolean(presetAnchor)} onClose={() => setPresetAnchor(null)}>
        {/* The catalogue's own defaults are always there, so this is also
            the way back to them; it showed nothing on a deployment with no
            saved presets (AG-78). */}
        <MenuItem
          onClick={() => {
            saveConfig(defaultConfig, BUILT_IN_PRESET)
            setPresetAnchor(null)
          }}
        >
          {BUILT_IN_PRESET}
        </MenuItem>
        {presets.filter((p) => p.name !== BUILT_IN_PRESET).map((p) => (
          <MenuItem key={p.id} onClick={() => applyPreset(p)}>{p.name}</MenuItem>
        ))}
        {!presets.some((p) => p.name !== BUILT_IN_PRESET) && (
          <MenuItem disabled sx={{ fontSize: 13 }}>No saved presets yet</MenuItem>
        )}
      </Menu>

      {duplicate && (
        <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(17,38,36,.25)', display: 'grid', placeItems: 'center', zIndex: 20 }}>
          <Box
            role="dialog"
            aria-label="File already uploaded"
            sx={{
              backgroundColor: 'background.paper', borderRadius: `${tokens.radius.lg}px`,
              boxShadow: tokens.shadow.float, p: 6, width: 460,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>Already in this region</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              {duplicate.file.name} is the same file as {duplicate.of.filename ?? 'an earlier upload'}, uploaded{' '}
              {formatDateTime(duplicate.of.created_at)} with {num(duplicate.of.plots)} plots. Uploading it again puts each of
              those plots in the region twice.
            </Typography>
            {removeIngest.error && (
              <Alert severity="error" sx={{ mb: 4 }}>{(removeIngest.error as Error).message}</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button variant="text" onClick={() => resolveDuplicate('skip')} disabled={resolving}>Don&apos;t upload</Button>
              <Button variant="outlined" onClick={() => resolveDuplicate('again')} disabled={resolving}>Add it again</Button>
              <Button onClick={() => resolveDuplicate('replace')} disabled={resolving}>
                {resolving ? 'Working…' : 'Replace the earlier upload'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {cancelling && (
        <Box
          onClick={() => !discarding && setCancelling(false)}
          sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(17,38,36,.25)', display: 'grid', placeItems: 'center', zIndex: 20 }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Cancel this upload"
            sx={{
              backgroundColor: 'background.paper', borderRadius: `${tokens.radius.lg}px`,
              boxShadow: tokens.shadow.float, p: 6, width: 440,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>Cancel this upload?</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              {read.length === 1 ? `${read[0]!.name} is` : `${read.length} files are`} already in{' '}
              {region?.name ?? 'the region'} but not validated. Remove {read.length === 1 ? 'it' : 'them'}, or
              keep {read.length === 1 ? 'it' : 'them'} there to run the checks later.
            </Typography>
            {removeIngest.error && (
              <Alert severity="error" sx={{ mb: 4 }}>{(removeIngest.error as Error).message}</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="text" onClick={() => setCancelling(false)} disabled={discarding}>Stay here</Button>
              <Button variant="outlined" onClick={() => goHome(true)} disabled={discarding}>Keep and leave</Button>
              <Button
                onClick={discardAndLeave}
                disabled={discarding}
                sx={{ backgroundColor: tokens.color.danger, '&:hover': { backgroundColor: '#8e3b28' } }}
              >
                {discarding ? 'Removing…' : 'Remove and leave'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {savingPreset && (
        <Box
          onClick={() => setSavingPreset(false)}
          sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(17,38,36,.25)', display: 'grid', placeItems: 'center', zIndex: 20 }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: 'background.paper', borderRadius: `${tokens.radius.lg}px`,
              boxShadow: tokens.shadow.float, p: 6, width: 380,
            }}
          >
            <Typography variant="h4" sx={{ mb: 2 }}>Save as preset</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
              The current checks and parameters, ready to load on the next run.
            </Typography>
            <TextField
              fullWidth autoFocus value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Cocoa CI · strict"
              inputProps={{ 'aria-label': 'Preset name' }}
              sx={{ mb: 5 }}
            />
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setSavingPreset(false)}>Cancel</Button>
              <Button
                disabled={!presetName.trim() || savePreset.isPending}
                onClick={() =>
                  savePreset.mutate(
                    { name: presetName.trim(), config },
                    {
                      onSuccess: () => {
                        saveConfig(config, presetName.trim())
                        setSavingPreset(false)
                        setPresetName('')
                      },
                    },
                  )
                }
              >
                Save preset
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  )
}

/** A file in the list, with the local echo of its mapping while a change is in flight. */
type Item = Omit<UploadItem, 'unmapped'> & { draft?: Record<string, string | null> }

/** The upload a file duplicates, as the API names it. */
interface DuplicateOf {
  id: string
  filename: string | null
  created_at: string
  plots: number
}

function matchesOf(item: Item) {
  const d = item.detail
  if (!d) return []
  return item.draft ? d.field_matches.map((m) => ({ ...m, column: item.draft![m.field] ?? null })) : d.field_matches
}

/** Required fields this file still needs a column for. */
function unmappedOf(item: Item): string[] {
  const optional = new Set(item.detail?.optional_fields ?? [])
  return matchesOf(item).filter((m) => !m.column && !optional.has(m.field)).map((m) => m.field)
}

function mappingOf(item: Item): Record<string, string | null> {
  return item.draft ?? Object.fromEntries(matchesOf(item).map((m) => [m.field, m.column]))
}

/** Same columns, whatever their order: the same mapping will do. */
function sameColumns(a: IngestDetail, b: IngestDetail): boolean {
  const key = (d: IngestDetail) => [...d.ingest.source_columns].sort().join('\u0000')
  return key(a) === key(b)
}

/**
 * Where these plots are going.
 *
 * Existing regions, or a new one by name — the same two things
 * `POST /regions` offers, because there is nowhere else a plot can land.
 * Locked once a file has been read: the plots are already in that region
 * and moving them is a different operation.
 */
function RegionPicker({
  regions, countries, value, onChange, newName, onNewName, newCountry, onNewCountry, onCreate, creating,
  disabled, missingCountry, settingCountry, onSetCountry,
}: {
  regions: { id: string; name: string; plot_count: number; country_code: string | null }[]
  countries: Country[]
  value: string
  onChange: (id: string) => void
  newName: string
  onNewName: (name: string) => void
  /** A region is created with its country: every plot in it is judged against that border. */
  newCountry: string
  onNewCountry: (code: string) => void
  onCreate: () => void
  creating: boolean
  disabled: boolean
  /** The chosen region has no country yet, so it is asked for here. */
  missingCountry: boolean
  settingCountry: boolean
  onSetCountry: (code: string) => void
}) {
  return (
    <Box>
      <Eyebrow sx={{ mb: 2 }}>Region</Eyebrow>
      {/*
        Both rows fill the column rather than sitting at their natural widths:
        a 280px select in a 440px column reads as an unfinished layout, and
        the name field beside it had nowhere to go but the next line anyway.
      */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          select
          size="small"
          fullWidth
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{ native: true }}
          inputProps={{ 'aria-label': 'Region' }}
        >
          <option value="">Choose a region…</option>
          {regions.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} · {num(r.plot_count)} polygons
            </option>
          ))}
        </TextField>
        {missingCountry && !disabled && (
          <Box>
            <CountryPicker
              countries={countries}
              value=""
              disabled={settingCountry}
              label="This region's country"
              onChange={(code) => code && onSetCountry(code)}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
              This region has no country yet. Every plot in it is checked against that country&rsquo;s border.
            </Typography>
          </Box>
        )}
        {!disabled && (
          <>
            <TextField
              size="small"
              fullWidth
              value={newName}
              placeholder="…or name a new one"
              onChange={(e) => onNewName(e.target.value)}
              inputProps={{ 'aria-label': 'New region name' }}
            />
            {newName.trim() && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                  <CountryPicker countries={countries} value={newCountry} onChange={onNewCountry} label="Country" />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!newName.trim() || !newCountry || creating}
                  onClick={onCreate}
                >
                  Create
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
